import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  { id: 'intro', label: 'はじめに' },
  { id: 'article1', label: '第1条　適用' },
  { id: 'article2', label: '第2条　利用登録' },
  { id: 'article3', label: '第3条　禁止事項' },
  { id: 'article4', label: '第4条　提供の停止等' },
  { id: 'article5', label: '第5条　免責事項' },
  { id: 'article6', label: '第6条　サービス内容の変更等' },
  { id: 'article7', label: '第7条　利用規約の変更' },
  { id: 'article8', label: '第8条　準拠法・裁判管轄' },
];

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState('intro');
  const [scrolled, setScrolled] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600;700&family=Noto+Serif+JP:wght@400;600;700&display=swap"
        rel="stylesheet"
      />

      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white border-b border-gray-200 shadow-sm' : 'bg-white border-b border-gray-200'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#102A43' }}>
              <i className="ri-time-line text-white text-base"></i>
            </div>
            <span className="text-lg font-bold tracking-tight" style={{ color: '#102A43', fontFamily: "'Noto Serif JP', serif" }}>
              AI納期
            </span>
          </Link>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <i className="ri-file-text-line text-base"></i>
            <span>利用規約</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10 flex gap-10">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100" style={{ backgroundColor: '#102A43' }}>
                <p className="text-xs font-semibold text-white tracking-wider uppercase">目次</p>
              </div>
              <nav className="py-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-all duration-150 cursor-pointer border-l-2 ${
                      activeSection === section.id
                        ? 'border-l-2 font-semibold'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    style={
                      activeSection === section.id
                        ? { borderLeftColor: '#102A43', color: '#102A43', backgroundColor: '#f0f4f8' }
                        : {}
                    }
                  >
                    {section.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <i className="ri-links-line text-gray-500"></i>
                関連ページ
              </p>
              <Link
                to="/privacy"
                className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900 py-1.5 cursor-pointer"
              >
                <i className="ri-shield-check-line text-gray-400"></i>
                プライバシーポリシー
              </Link>
            </div>

            <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-xs font-semibold text-blue-700 mb-1">お問い合わせ</p>
                  <p className="text-xs text-blue-600">ご不明な点は<br />support@ai-noki.com<br />までご連絡ください</p>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* Document Header */}
            <div className="px-8 py-8 border-b border-gray-100" style={{ backgroundColor: '#102A43' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
                  <i className="ri-file-text-line text-white text-xl"></i>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    利用規約
                  </h1>
                  <p className="text-blue-200 text-sm mt-0.5">Terms of Service</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-4">
                <div className="flex items-center gap-1.5 text-blue-100 text-sm">
                  <i className="ri-calendar-line text-blue-300"></i>
                  <span>制定日：2025年4月1日</span>
                </div>
              </div>
            </div>

            <div className="px-8 py-8 space-y-10">
              {/* Intro */}
              <section id="intro">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">サービス名</span>
                      <p className="font-semibold text-gray-800 mt-0.5">AI納期</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">運営者</span>
                      <p className="font-semibold text-gray-800 mt-0.5">個人事業主 小助川</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">連絡先</span>
                      <a
                        href="mailto:support@ai-noki.com"
                        className="font-medium mt-0.5 block hover:underline"
                        style={{ color: '#102A43' }}
                      >
                        support@ai-noki.com
                      </a>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed text-sm">
                  本利用規約（以下「本規約」）は、個人事業主 小助川（以下「運営者」）が提供するサービス「AI納期」（以下「本サービス」）の利用条件を定めるものです。ユーザーの皆さまには、本規約に同意した上でご利用いただきます。
                </p>
              </section>

              <hr className="border-gray-100" />

              {/* Article 1 */}
              <section id="article1">
                <ArticleHeading number="第1条" title="適用" />
                <p className="text-gray-700 text-sm leading-relaxed">
                  本規約は、ユーザーと運営者との間の本サービスの利用に関わる一切の関係に適用されます。運営者は本サービスに関し、本規約のほか、ご利用にあたってのルールを定めることがあります。これらのルールは、本規約の一部を構成するものとします。
                </p>
              </section>

              <hr className="border-gray-100" />

              {/* Article 2 */}
              <section id="article2">
                <ArticleHeading number="第2条" title="利用登録" />
                <p className="text-gray-700 text-sm leading-relaxed mb-5">
                  本サービスへの登録を申請した方が、運営者の定める方法によって利用登録を申請し、運営者がこれを承認することによって、利用登録が完了するものとします。運営者は、利用登録の申請者に以下の事由があると判断した場合、利用登録の申請を承認しないことがあります。
                </p>
                <NumberedList
                  items={[
                    '虚偽の事項を届け出た場合',
                    '本規約に違反したことがある者からの申請である場合',
                    'その他、運営者が利用登録を相当でないと判断した場合',
                  ]}
                />
              </section>

              <hr className="border-gray-100" />

              {/* Article 3 */}
              <section id="article3">
                <ArticleHeading number="第3条" title="禁止事項" />
                <p className="text-gray-700 text-sm leading-relaxed mb-5">
                  ユーザーは、本サービスの利用にあたり、以下の行為をしてはなりません。
                </p>
                <div className="border border-red-100 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border-b border-red-100">
                    <i className="ri-forbid-line text-red-500 text-sm"></i>
                    <p className="text-sm font-semibold text-red-700">以下の行為は禁止されています</p>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {[
                      '法令または公序良俗に違反する行為',
                      '犯罪行為に関連する行為',
                      '本サービスのサーバーまたはネットワークの機能を破壊したり、妨害したりする行為',
                      '本サービスの運営を妨害するおそれのある行為',
                      '他のユーザーに関する個人情報等を収集または蓄積する行為',
                      '不正アクセスをし、またはこれを試みる行為',
                      '他のユーザーに成りすます行為',
                      '本サービスに関連して、反社会的勢力に対して直接または間接に利益を供与する行為',
                      'その他、運営者が不適切と判断する行為',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 px-4 py-3">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-red-500 bg-red-50 mt-0.5">
                          {i + 1}
                        </span>
                        <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <hr className="border-gray-100" />

              {/* Article 4 */}
              <section id="article4">
                <ArticleHeading number="第4条" title="本サービスの提供の停止等" />
                <p className="text-gray-700 text-sm leading-relaxed mb-5">
                  運営者は、以下のいずれかの事由があると判断した場合、ユーザーに事前に通知することなく本サービスの全部または一部の提供を停止または中断することができるものとします。
                </p>
                <NumberedList
                  items={[
                    '本サービスにかかるコンピュータシステムの保守点検または更新を行う場合',
                    '地震、落雷、火災、停電または天災などの不可抗力により、本サービスの提供が困難となった場合',
                    'コンピュータまたは通信回線等が事故により停止した場合',
                    'その他、運営者が本サービスの提供が困難と判断した場合',
                  ]}
                />
              </section>

              <hr className="border-gray-100" />

              {/* Article 5 */}
              <section id="article5">
                <ArticleHeading number="第5条" title="免責事項" />
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <i className="ri-alert-line text-amber-500 mt-0.5 flex-shrink-0 text-base"></i>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    運営者の債務不履行責任は、運営者の故意または重過失によらない場合には免責されるものとします。本サービスに関して、ユーザーと他のユーザーまたは第三者との間において生じた取引、連絡または紛争等については、運営者は一切責任を負いません。
                  </p>
                </div>
              </section>

              <hr className="border-gray-100" />

              {/* Article 6 */}
              <section id="article6">
                <ArticleHeading number="第6条" title="サービス内容の変更等" />
                <p className="text-gray-700 text-sm leading-relaxed">
                  運営者は、ユーザーへの事前の告知をもって、本サービスの内容を変更、追加または廃止することがあり、ユーザーはこれを承諾するものとします。
                </p>
              </section>

              <hr className="border-gray-100" />

              {/* Article 7 */}
              <section id="article7">
                <ArticleHeading number="第7条" title="利用規約の変更" />
                <p className="text-gray-700 text-sm leading-relaxed">
                  運営者は、必要と判断した場合には、ユーザーに通知することなくいつでも本規約を変更することができるものとします。なお、本規約の変更後、本サービスの利用を開始した場合には、当該ユーザーは変更後の規約に同意したものとみなします。
                </p>
              </section>

              <hr className="border-gray-100" />

              {/* Article 8 */}
              <section id="article8">
                <ArticleHeading number="第8条" title="準拠法・裁判管轄" />
                <p className="text-gray-700 text-sm leading-relaxed mb-5">
                  本規約の解釈にあたっては、日本法を準拠法とします。本サービスに関して紛争が生じた場合には、運営者の所在地を管轄する裁判所を専属的合意管轄とします。
                </p>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100" style={{ backgroundColor: '#102A43' }}>
                    <p className="text-sm font-semibold text-white flex items-center gap-2">
                      <i className="ri-customer-service-2-line"></i>
                      運営者情報・お問い合わせ窓口
                    </p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {[
                      { label: 'サービス名', value: 'AI納期', icon: 'ri-time-line' },
                      { label: '運営者', value: '個人事業主 小助川', icon: 'ri-user-line' },
                      { label: 'メールアドレス', value: 'support@ai-noki.com', icon: 'ri-mail-line', isEmail: true },
                    ].map((row) => (
                      <div key={row.label} className="flex items-center gap-4 px-5 py-3.5">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={`${row.icon} text-sm text-gray-400`}></i>
                        </div>
                        <span className="text-xs font-medium text-gray-500 w-28 flex-shrink-0">{row.label}</span>
                        {row.isEmail ? (
                          <a
                            href={`mailto:${row.value}`}
                            className="text-sm font-medium hover:underline"
                            style={{ color: '#102A43' }}
                          >
                            {row.value}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-800">{row.value}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Footer note */}
              <div className="pt-4 border-t border-gray-100 text-center">
                <p className="text-xs text-gray-400">
                  制定日：2025年4月1日
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  &copy; 2025 AI納期 / 個人事業主 小助川. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="mt-10 border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center rounded" style={{ backgroundColor: '#102A43' }}>
              <i className="ri-time-line text-white text-xs"></i>
            </div>
            <span className="text-sm font-semibold" style={{ color: '#102A43' }}>AI納期</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <Link to="/privacy" className="hover:text-gray-800 cursor-pointer">プライバシーポリシー</Link>
            <span className="text-gray-300">|</span>
            <Link to="/terms" className="hover:text-gray-800 cursor-pointer">利用規約</Link>
            <span className="text-gray-300">|</span>
            <a href="mailto:support@ai-noki.com" className="hover:text-gray-800 cursor-pointer">お問い合わせ</a>
          </div>
          <p className="text-xs text-gray-400">&copy; 2025 個人事業主 小助川</p>
        </div>
      </footer>
    </div>
  );
}

function ArticleHeading({ number, title }: { number: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div
        className="px-3 py-1 rounded text-xs font-bold text-white whitespace-nowrap"
        style={{ backgroundColor: '#102A43' }}
      >
        {number}
      </div>
      <h2
        className="text-lg font-bold text-gray-900"
        style={{ fontFamily: "'Noto Serif JP', serif" }}
      >
        {title}
      </h2>
    </div>
  );
}

function NumberedList({ items }: { items: string[] }) {
  return (
    <ol className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
          <span
            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white mt-0.5"
            style={{ backgroundColor: '#102A43' }}
          >
            {i + 1}
          </span>
          <span className="leading-relaxed pt-0.5">{item}</span>
        </li>
      ))}
    </ol>
  );
}
